// src/lib/ordin904.ts — nomenclatorul Ordinului MDLPA nr. 904/2023 (structura bazei de date
// GIS pentru documentațiile de urbanism) ca DATE, transcris din specificațiile tehnice ale
// platformei Date Locale (datelocale.mdlpa.ro/ro/about/tehnic_planurb/ — autoritatea la care
// se încarcă efectiv livrabilul) + lista straturilor minimale din caietele de sarcini.
//
// PUR (fără DB) — importabil și din *.client.ts (dropdown-ul de mapare din #/admin).
// ⚠️ Numele câmpurilor sunt EXACTE (case-sensitive) — validatorul MDLPA le respinge altfel.
// Precizări oficiale de implementare: Detaliu_RU e varchar (NU decimal); PlanSpatial e
// MultiPolygon (UAT-uri cu mai multe trupuri); datele în format ISO YYYY-MM-DD (nu DATETIME);
// proiecția OBLIGATORIE EPSG:3844 (Stereo 70); GeoPackage 1.3.1 (1.2.0 acceptat).

export interface Ordin904Attr {
	name: string // numele EXACT al câmpului în GPKG-ul livrat
	type: "varchar" | "float" | "date" // tipul din spec (datele = text ISO YYYY-MM-DD)
	required: boolean
}

export interface Ordin904Class {
	name: string // numele EXACT al stratului în GPKG (ex. "ZFPropusa")
	label: string // eticheta RO pentru UI
	geometry: "Point" | "LineString" | "Polygon" | "MultiPolygon" | "none" // none = tabel atribut
	required: boolean // strat minimal obligatoriu pentru un PUG (lista din caietele de sarcini)
	attrs: Ordin904Attr[]
}

const v = (name: string, required = true): Ordin904Attr => ({ name, type: "varchar", required })
const f = (name: string, required = true): Ordin904Attr => ({ name, type: "float", required })
const d = (name: string, required = true): Ordin904Attr => ({ name, type: "date", required })

export const ORDIN_904_CLASSES: readonly Ordin904Class[] = [
	{
		name: "PlanSpatial",
		label: "Planul spațial (conturul documentației)",
		geometry: "MultiPolygon",
		required: true,
		attrs: [
			v("SIRUTA"),
			v("Judet"),
			v("UAT"),
			v("NivelPlan"),
			v("TitluPlan"),
			v("HCL"),
			d("Data_aprob"),
			d("Data_exp"),
			v("Stadiu"),
			f("ArieADM"),
			f("ArieINT"),
			f("AriePlan"),
		],
	},
	{
		name: "LimitaIntravilanExistenta",
		label: "Limita intravilanului existent",
		geometry: "Polygon",
		required: true,
		attrs: [v("SIRUTA"), v("Judet"), v("UAT"), d("Revizie"), v("Sursa_revizie"), f("ArieINT")],
	},
	{
		name: "LimitaIntravilanPropusa",
		label: "Limita intravilanului propus",
		geometry: "Polygon",
		required: true,
		attrs: [v("SIRUTA"), v("Judet"), v("UAT"), d("Revizie"), f("ArieINT")],
	},
	{
		name: "ZFExistenta",
		label: "Zonificarea funcțională existentă",
		geometry: "Polygon",
		required: true,
		attrs: [
			v("Cod_ZF"),
			v("Tip_ZF"),
			v("Cod_SZF"),
			v("Tip_SZF"),
			v("HILUCS_N1"),
			v("HILUCS_N2", false),
			v("HILUCS_N3", false),
			f("Arie_ZF"),
			d("Data_aprob", false),
			d("Data_exp"),
		],
	},
	{
		name: "ZFPropusa",
		label: "Zonificarea funcțională propusă",
		geometry: "Polygon",
		required: true,
		attrs: [
			v("Cod_ZF"),
			v("Tip_ZF"),
			v("Cod_SZF"),
			v("Tip_SZF"),
			v("HILUCS_N1"),
			v("HILUCS_N2", false),
			v("HILUCS_N3", false),
			f("Arie_ZF"),
			d("Data_aprob"),
			d("Data_exp"),
		],
	},
	{
		name: "ZonaReglementareSuplimentara",
		label: "Zone de reglementare suplimentară (protecții, interdicții)",
		geometry: "Polygon",
		required: true,
		attrs: [
			v("Cod_ZRS"),
			v("Tip_ZRS"),
			v("Cod_ZRS_D"),
			v("Tip_ZRS_D"),
			f("Arie_ZRS"),
			v("Sursa_ZRS"),
			v("Detalii_sursa_ZRS"),
			d("Data_aprob"),
			d("Data_exp"),
		],
	},
	{
		name: "ReglementariUrbanisticePunct",
		label: "Reglementări urbanistice punctuale",
		geometry: "Point",
		required: true,
		// Precizare oficială: Detaliu_RU e varchar, NU decimal.
		attrs: [v("ID_RU"), v("Tip_RU"), v("Detaliu_RU")],
	},
	{
		name: "CaiComunicatie",
		label: "Căi de comunicație",
		geometry: "LineString",
		required: true,
		attrs: [
			v("Cod_CCom", false),
			v("Tip_CCom"),
			v("Subtip_CCom"),
			v("Detaliu_CCom", false),
			v("Stadiu_CCom"),
			f("Lungime"),
		],
	},
	{
		name: "ReteleTehnicoEdilitare",
		label: "Rețele tehnico-edilitare",
		geometry: "LineString",
		required: true,
		attrs: [
			v("Cod_RTE", false),
			v("Tip_RTE"),
			v("Subtip_RTE"),
			v("Capacitate_RTE", false),
			v("Detaliu_RTE", false),
			v("Stadiu_RTE"),
			f("Lungime"),
		],
	},
	{
		name: "RegulamentLocalUrbanism",
		label: "RLU — zone funcționale (tabel, fără geometrie)",
		geometry: "none",
		required: true,
		attrs: [v("Cod_ZF_ZRS"), v("Tip_zona"), v("Tip_ZF_ZRS"), v("Desc_ZF_ZRS")],
	},
	{
		name: "RegulamentLocalUrbanismDetaliat",
		label: "RLU detaliat — indicatori per subzonă (tabel, fără geometrie)",
		geometry: "none",
		required: true,
		// POT/CUT/Clad_Hmax sunt varchar prin precizarea oficială (textul liber din RLU e acceptat).
		attrs: [
			v("Cod_SZF_ZRS_D"),
			v("Tip_zona"),
			v("Tip_SZF_ZRS_D"),
			v("Desc_SZF_ZRS_D"),
			v("Utiliz_admise", false),
			v("Utiliz_admise_cond", false),
			v("Utiliz_interzise", false),
			v("Parc_desc", false),
			v("Clad_aliniament", false),
			v("Clad_limite_parcele", false),
			v("Clad_poz", false),
			v("Parc_acces", false),
			v("Parc_stat", false),
			v("Clad_Hmax", false),
			v("Clad_RHmax", false),
			v("Clad_aspect_ext", false),
			v("Parc_edilitar", false),
			v("Parc_sp_verzi", false),
			v("Parc_imprejmuiri", false),
			v("POT", false),
			v("CUT", false),
		],
	},
] as const

export const ORDIN_904_BY_NAME: ReadonlyMap<string, Ordin904Class> = new Map(
	ORDIN_904_CLASSES.map((c) => [c.name, c]),
)

/** Clasele SPAȚIALE (cele care se mapează pe straturi GIS; tabelele RLU vin din metadata.rlu). */
export const ORDIN_904_SPATIAL: readonly Ordin904Class[] = ORDIN_904_CLASSES.filter(
	(c) => c.geometry !== "none",
)

/** Proiecția OBLIGATORIE a livrabilului (Stereo 70). */
export const ORDIN_904_EPSG = 3844

/** Doar litere și cifre, minuscule — forma în care comparăm numele de straturi cu nomenclatorul. */
function alnum(s: string): string {
	return s.toLowerCase().replace(/[^a-z0-9]/g, "")
}

/**
 * Potrivire FERMĂ pe numele oficial al clasei (nomenclatorul Ordin 904): straturile livrate deja în
 * format 904 vin cu slug-uri ca `1_planspatial`, `001_zfpropusa`, `bz_colti_46439_pug_20260820_zfexistenta`,
 * `nt_roman_…_mdlpa_reteletehnicoedilitare` sau `planspatial`, iar numele afișat e chiar clasa
 * („PlanSpatial"). Se acceptă: ultimul segment al slug-ului (sau tot slug-ul), respectiv numele
 * afișat, egal cu numele oficial — inclusiv varianta fără „a" final („limitaintravilanexistent",
 * Crețeni). Întoarce "" când nu e o potrivire sigură; euristicile pe cuvinte-cheie rămân separate.
 */
export function match_904_official(layer_slug: string, layer_name = ""): string {
	const candidates = new Set<string>()
	const slug = alnum(layer_slug)
	if (slug) {
		candidates.add(slug)
	}
	const parts = layer_slug
		.toLowerCase()
		.split(/[^a-z0-9]+/)
		.filter(Boolean)
	if (parts.length > 0) {
		candidates.add(parts[parts.length - 1])
	}
	const name = alnum(layer_name)
	if (name) {
		candidates.add(name)
	}
	for (const cls of ORDIN_904_SPATIAL) {
		const official = cls.name.toLowerCase()
		for (const c of candidates) {
			// egal, sau prefix care lipsește cel mult ultima literă (existent/existentă, propus/propusă)
			if (c === official || (official.startsWith(c) && c.length >= official.length - 1)) {
				return cls.name
			}
		}
	}
	return ""
}

/**
 * Sugerează clasa 904 pentru un strat intern: întâi potrivirea FERMĂ pe numele oficial
 * (`match_904_official` — sigură, aplicabilă automat), apoi euristici pe cuvinte-cheie din slug
 * (doar pre-completare; maparea o confirmă omul).
 */
export function suggest_904_class(layer_slug: string, layer_name = ""): string {
	const official = match_904_official(layer_slug, layer_name)
	if (official) {
		return official
	}
	const s = layer_slug.toLowerCase()
	if (/(intravilan).*(existent)|(existent).*(intravilan)/.test(s)) {
		return "LimitaIntravilanExistenta"
	}
	if (/intravilan/.test(s)) {
		return "LimitaIntravilanPropusa"
	}
	if (/(zonif|zone_func|zf).*(existent)|(existent).*(zonif)/.test(s)) {
		return "ZFExistenta"
	}
	if (/utr|zonif|zone_func|^zf/.test(s)) {
		return "ZFPropusa"
	}
	if (/protect|interdict|zrs|reglementare_supl/.test(s)) {
		return "ZonaReglementareSuplimentara"
	}
	if (/drum|strad|strazi|cai_com|comunicatie|cf|ferat/.test(s)) {
		return "CaiComunicatie"
	}
	if (/retel|edilitar|apa|canal|gaz|electric/.test(s)) {
		return "ReteleTehnicoEdilitare"
	}
	if (/monument|reper|reglementari_punct/.test(s)) {
		return "ReglementariUrbanisticePunct"
	}
	if (/plan_spatial|contur|limita_adm|administrativ_teritoriala_uat/.test(s)) {
		return "PlanSpatial"
	}
	return ""
}
